/**
 * Deterministic Social Security retirement benefit estimate.
 *
 * This is intentionally client-safe so onboarding can preview the same estimate
 * that the API writes as a future income stream.
 */

/** 2024 SSA bend points for PIA calculation. */
const BEND_POINT_1 = 1_174;
const BEND_POINT_2 = 7_078;

const RATE_1 = 0.9;
const RATE_2 = 0.32;
const RATE_3 = 0.15;

/** 2024 Social Security taxable wage base. */
const SS_WAGE_BASE = 168_600;

const DELAYED_CREDIT_PER_YEAR = 0.08;
const EARLY_REDUCTION_PER_MONTH_FIRST_36 = 5 / 9 / 100;
const EARLY_REDUCTION_PER_MONTH_BEYOND_36 = 5 / 12 / 100;

export interface SocialSecurityEstimate {
  earlyMonthly: number;
  fraMonthly: number;
  lateMonthly: number;
  fullRetirementAge: number;
  fraYear: number;
}

function getFullRetirementAge(birthYear: number): number {
  if (birthYear <= 1937) return 65;
  if (birthYear <= 1942) return 66;
  if (birthYear <= 1954) return 66;
  if (birthYear <= 1959) return 67;
  return 67;
}

function calculatePIA(aime: number): number {
  let pia = 0;

  if (aime <= BEND_POINT_1) {
    pia = aime * RATE_1;
  } else if (aime <= BEND_POINT_2) {
    pia = BEND_POINT_1 * RATE_1 + (aime - BEND_POINT_1) * RATE_2;
  } else {
    pia =
      BEND_POINT_1 * RATE_1 +
      (BEND_POINT_2 - BEND_POINT_1) * RATE_2 +
      (aime - BEND_POINT_2) * RATE_3;
  }

  return Math.floor(pia * 10) / 10;
}

function applyEarlyReduction(pia: number, monthsBefore: number): number {
  if (monthsBefore <= 0) return pia;

  const first36 = Math.min(monthsBefore, 36);
  const beyond36 = Math.max(0, monthsBefore - 36);
  const reduction =
    first36 * EARLY_REDUCTION_PER_MONTH_FIRST_36 +
    beyond36 * EARLY_REDUCTION_PER_MONTH_BEYOND_36;

  return pia * (1 - reduction);
}

function applyDelayedCredits(pia: number, monthsAfter: number): number {
  if (monthsAfter <= 0) return pia;
  return pia * (1 + (DELAYED_CREDIT_PER_YEAR / 12) * monthsAfter);
}

export function estimateSocialSecurity(
  currentAnnualSalary: number,
  birthYear: number,
): SocialSecurityEstimate {
  const cappedAnnual = Math.min(currentAnnualSalary, SS_WAGE_BASE);
  const aime = cappedAnnual / 12;
  const pia = calculatePIA(aime);
  const fra = getFullRetirementAge(birthYear);
  const fraYear = birthYear + fra;

  const monthsEarly = (fra - 62) * 12;
  const monthsDelayed = (70 - fra) * 12;

  return {
    earlyMonthly: Math.round(applyEarlyReduction(pia, monthsEarly)),
    fraMonthly: Math.round(pia),
    lateMonthly: Math.round(applyDelayedCredits(pia, monthsDelayed)),
    fullRetirementAge: fra,
    fraYear,
  };
}
